import { useState, useEffect } from 'react';
import { usePermissions } from '../context/PermissionsContext';
import api from '../services/api';

export default function ClassesPage() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id, name, description, ... } | 'new'
  const [form, setForm] = useState({ name: '', description: '', calibrationFrequencyDays: '', criterioAceitacao: '', criterioAprovacao: 'herdar', propagarFrequencia: false });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, name }
  const [toast, setToast] = useState('');

  const { can } = usePermissions();
  const canEdit = can('classes', 'edit');

  useEffect(() => { loadClasses(); }, []);

  const loadClasses = async () => {
    try {
      const res = await api.get('/instrument-classes');
      setClasses(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing('new');
    setForm({ name: '', description: '', calibrationFrequencyDays: '', criterioAceitacao: '', criterioAprovacao: 'herdar', propagarFrequencia: false });
    setError('');
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name,
      description: c.description || '',
      calibrationFrequencyDays: c.calibrationFrequencyDays ?? '',
      criterioAceitacao: c.criterioAceitacao || '',
      criterioAprovacao: c.criterioAprovacao || 'herdar',
      propagarFrequencia: false,
    });
    setError('');
  };

  const cancelEdit = () => { setEditing(null); setError(''); };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Nome obrigatório'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        name: form.name,
        description: form.description,
        calibrationFrequencyDays: form.calibrationFrequencyDays !== '' ? parseInt(form.calibrationFrequencyDays) : null,
        criterioAceitacao: form.criterioAceitacao,
        criterioAprovacao: form.criterioAprovacao,
      };
      if (editing === 'new') {
        const res = await api.post('/instrument-classes', payload);
        setClasses(prev => [...prev, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        const res = await api.patch(`/instrument-classes/${editing._id}`, { ...payload, propagarFrequencia: form.propagarFrequencia });
        setClasses(prev => prev.map(c => c._id === editing._id ? res.data : c));
        if (form.propagarFrequencia && res.data.propagados > 0) {
          setToast(`Frequência propagada para ${res.data.propagados} instrumento(s).`);
          setTimeout(() => setToast(''), 4000);
        }
      }
      setEditing(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/instrument-classes/${confirmDelete._id}`);
      setClasses(prev => prev.filter(c => c._id !== confirmDelete._id));
      setConfirmDelete(null);
    } catch (err) {
      setConfirmDelete(null);
      setError(err.response?.data?.error || 'Erro ao excluir');
    }
  };

  if (loading) return <div className="p-6 text-slate-400">Carregando...</div>;

  return (
    <div className="p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Classes de Instrumentos</h1>
          <p className="text-sm text-slate-500 mt-1">
            Agrupe instrumentos por tipo para herdar estratégias de calibração
          </p>
        </div>
        {canEdit && (
          <button
            onClick={openNew}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors"
          >
            + Nova Classe
          </button>
        )}
      </div>

      {error && !editing && (
        <div className="mb-4 p-3 bg-red-950/40 border border-red-700/50 rounded-lg text-red-300 text-sm">{error}</div>
      )}
      {toast && (
        <div className="mb-4 p-3 bg-emerald-950/40 border border-emerald-700/50 rounded-lg text-emerald-300 text-sm">{toast}</div>
      )}

      {/* Modal de criação/edição */}
      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl p-6 w-full max-w-md mx-4 space-y-4">
            <h3 className="text-base font-semibold text-white">
              {editing === 'new' ? 'Nova Classe' : `Editar — ${editing.name}`}
            </h3>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Nome *</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Balança, Termômetro, Manômetro..."
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-[var(--text-base)] placeholder-slate-500 outline-none focus:border-blue-500"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1">Descrição</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Descrição opcional da classe..."
                rows={2}
                className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-[var(--text-base)] placeholder-slate-500 outline-none focus:border-blue-500 resize-none"
              />
            </div>

            <div className="border-t border-[var(--surface-4)] pt-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Padrões de Calibração</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Frequência padrão (dias)</label>
                  <input
                    type="number"
                    min="1"
                    value={form.calibrationFrequencyDays}
                    onChange={e => setForm(f => ({ ...f, calibrationFrequencyDays: e.target.value }))}
                    placeholder="Ex: 365"
                    className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-[var(--text-base)] placeholder-slate-500 outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1">Critério de aceitação</label>
                  <input
                    type="text"
                    value={form.criterioAceitacao}
                    onChange={e => setForm(f => ({ ...f, criterioAceitacao: e.target.value }))}
                    placeholder="Ex: ±0,5% ou 20× resolução"
                    className="w-full bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-lg px-3 py-2 text-sm text-[var(--text-base)] placeholder-slate-500 outline-none focus:border-blue-500"
                  />
                </div>
              </div>
              <p className="text-[10px] text-slate-600 mt-2">Estes valores serão sugeridos automaticamente ao vincular um instrumento a esta classe.</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-400 mb-1.5">Critério de aprovação (incerteza)</label>
              <div className="flex gap-1">
                {[
                  { key: 'herdar', label: 'Herdar tenant', desc: 'Usa configuração global' },
                  { key: 'simples', label: 'Simples', desc: '|erro| ≤ EMP' },
                  { key: 'conservador', label: 'Conservador', desc: '|erro|+U ≤ EMP (ILAC G8)' },
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    title={opt.desc}
                    onClick={() => setForm(f => ({ ...f, criterioAprovacao: opt.key }))}
                    className={`flex-1 px-2 py-1.5 rounded text-[10px] font-medium transition-colors border ${
                      form.criterioAprovacao === opt.key
                        ? 'bg-blue-700 text-white border-blue-600'
                        : 'bg-[var(--surface-1)] text-slate-400 border-[var(--surface-4)] hover:text-white'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-slate-600 mt-1">
                {form.criterioAprovacao === 'herdar' && 'Usa o critério definido nas configurações do tenant.'}
                {form.criterioAprovacao === 'simples' && 'Aprovado se |erro| ≤ EMP (não considera incerteza expandida).'}
                {form.criterioAprovacao === 'conservador' && 'Aprovado se |erro| + U ≤ EMP — recomendado ILAC G8.'}
              </p>
            </div>

            {editing !== 'new' && form.calibrationFrequencyDays !== '' && (
              <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-lg bg-[var(--surface-1)] border border-[var(--surface-4)] hover:border-blue-700/50 transition-colors">
                <input
                  type="checkbox"
                  checked={form.propagarFrequencia}
                  onChange={e => setForm(f => ({ ...f, propagarFrequencia: e.target.checked }))}
                  className="mt-0.5 accent-blue-500"
                />
                <div>
                  <p className="text-xs font-medium text-slate-300">Propagar frequência para todos os instrumentos desta classe</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">Atualiza o campo "Frequência" de todos os {editing.instrumentCount ?? 0} instrumento(s) vinculados.</p>
                </div>
              </label>
            )}

            {error && <p className="text-red-400 text-xs">{error}</p>}
            <div className="flex gap-2 justify-end pt-1">
              <button onClick={cancelEdit} className="px-4 py-2 text-xs text-slate-400 hover:text-white border border-[var(--surface-4)] rounded-lg transition-colors">
                Cancelar
              </button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-xs bg-blue-700 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 transition-colors">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmação de exclusão */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-[var(--surface-2)] border border-[var(--surface-4)] rounded-xl p-6 w-full max-w-sm mx-4 space-y-4">
            <h3 className="text-base font-semibold text-white">Excluir classe?</h3>
            <p className="text-sm text-slate-400">
              <span className="text-white font-medium">{confirmDelete.name}</span> será removida permanentemente.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-xs text-slate-400 hover:text-white border border-[var(--surface-4)] rounded-lg transition-colors">
                Cancelar
              </button>
              <button onClick={handleDelete} className="px-4 py-2 text-xs bg-red-700 hover:bg-red-600 text-white rounded-lg transition-colors">
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista */}
      {classes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-slate-500">
          <span className="text-4xl mb-3">🏷️</span>
          <p className="text-sm">Nenhuma classe cadastrada.</p>
          {canEdit && (
            <button onClick={openNew} className="mt-3 text-xs text-blue-400 hover:text-blue-300 underline">
              Criar primeira classe
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {classes.map(c => (
            <div
              key={c._id}
              className="flex items-center justify-between bg-[var(--surface-3)] border border-[var(--surface-4)] rounded-xl px-5 py-4 hover:border-[var(--surface-5)] transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-white">{c.name}</span>
                  <span className="text-[10px] px-2 py-0.5 bg-[var(--surface-4)] text-slate-400 rounded-full">
                    {c.instrumentCount} instrumento{c.instrumentCount !== 1 ? 's' : ''}
                  </span>
                </div>
                {c.description && (
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{c.description}</p>
                )}
                {(c.calibrationFrequencyDays || c.criterioAceitacao || c.criterioAprovacao) && (
                  <div className="flex gap-3 mt-1.5 flex-wrap">
                    {c.calibrationFrequencyDays && (
                      <span className="text-[10px] text-blue-400 bg-blue-950/30 border border-blue-800/40 px-2 py-0.5 rounded-full">
                        ⏱ {c.calibrationFrequencyDays}d
                      </span>
                    )}
                    {c.criterioAceitacao && (
                      <span className="text-[10px] text-emerald-400 bg-emerald-950/30 border border-emerald-800/40 px-2 py-0.5 rounded-full">
                        ✓ {c.criterioAceitacao}
                      </span>
                    )}
                    {c.criterioAprovacao && c.criterioAprovacao !== 'herdar' && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                        c.criterioAprovacao === 'conservador'
                          ? 'text-amber-400 bg-amber-950/30 border-amber-800/40'
                          : 'text-slate-400 bg-slate-800/30 border-slate-700/40'
                      }`}>
                        {c.criterioAprovacao === 'conservador' ? 'ILAC G8' : 'Simples'}
                      </span>
                    )}
                  </div>
                )}
              </div>
              {canEdit && (
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => openEdit(c)}
                    className="text-xs text-slate-500 hover:text-slate-200 transition-colors px-2 py-1"
                  >
                    ✏️ Editar
                  </button>
                  <button
                    onClick={() => setConfirmDelete(c)}
                    className="text-xs text-slate-500 hover:text-red-400 transition-colors px-2 py-1"
                  >
                    🗑️
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
